import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, RotateCcw, Sun, Moon, Activity, AlertCircle } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Tooltip from "@radix-ui/react-tooltip";
import { BridgeApi, BusApi } from "../lib/api";
import { qk } from "../lib/query";
import { ensureStreamOpen, useStreamHealth, useStreamEvent } from "../lib/sse";
import { useTheme } from "../lib/theme";
import { useToast } from "../lib/toast";
import { cn } from "../lib/cn";
import { pagesById, type PageId } from "../pages/registry";
import { Kbd } from "./primitives";

function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function Topbar({
  page,
  onCmdK,
}: {
  page: PageId;
  onCmdK: () => void;
}) {
  const clock = useClock();
  const { theme, toggle } = useTheme();
  const toast = useToast();
  const qc = useQueryClient();

  // Status (initial fetch; SSE keeps it fresh)
  const { data: bridgeStatus } = useQuery({
    queryKey: qk.bridge.status,
    queryFn: BridgeApi.status,
    refetchInterval: 15000,
  });
  const { data: busHealth } = useQuery({
    queryKey: qk.bus.health,
    queryFn: BusApi.health,
    refetchInterval: 15000,
  });

  // Pulse on any incoming event
  const [pulseAt, setPulseAt] = useState(0);
  useStreamEvent(["log", "request", "bus", "spawn", "status", "config"], () => {
    setPulseAt(Date.now());
  });
  useStreamEvent("status", (ev) => {
    if (ev.type === "status") {
      qc.setQueryData(qk.bridge.status, { running: ev.bridge });
      qc.setQueryData(qk.bus.health, (prev: { healthy?: boolean } | undefined) => ({
        ...(prev ?? {}),
        healthy: ev.bus,
      }));
    }
  });

  useEffect(() => { ensureStreamOpen(); }, []);

  const restart = useMutation({
    mutationFn: BridgeApi.restart,
    onSuccess: () => {
      toast.success("agent-bridge restart issued", "systemd will reload the daemon");
      qc.invalidateQueries({ queryKey: qk.bridge.status });
    },
    onError: (err) => toast.error("Restart failed", String(err)),
  });

  const stream = useStreamHealth();
  const live = stream.alive && Date.now() - pulseAt < 8000;

  const pageDef = pagesById[page];
  const Icon = pageDef?.icon;

  return (
    <Tooltip.Provider delayDuration={200}>
      <header className="h-11 border-b border-border bg-surface/60 backdrop-blur-sm flex items-center px-3 gap-3 flex-shrink-0">
        {/* Breadcrumb / page title */}
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className="size-3.5 text-fg-subtle" />}
          <span className="text-sm font-medium text-fg truncate">{pageDef?.label ?? "Agent Fleet"}</span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Command palette */}
        <button
          onClick={onCmdK}
          className="hidden sm:flex items-center gap-2 h-7 px-2 rounded-md bg-surface-2 hover:bg-surface-elevated border border-border text-fg-muted hover:text-fg text-xs transition-colors ring-focus"
        >
          <Search className="size-3" />
          <span>Quick jump</span>
          <Kbd>⌘K</Kbd>
        </button>

        {/* Live pulse */}
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <div className="flex items-center gap-1.5 h-7 px-2 rounded-md bg-surface-2 border border-border">
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  live ? "bg-success animate-pulse-dot" : "bg-fg-subtle",
                )}
              />
              <span className="text-[11px] font-medium text-fg-muted">{live ? "live" : "idle"}</span>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="bottom"
              sideOffset={6}
              className="panel-elevated px-2.5 py-1.5 text-xs text-fg shadow-md max-w-xs"
            >
              <div>SSE: {stream.alive ? "connected" : "disconnected"}</div>
              <div className="text-fg-muted mt-0.5">
                Last event {pulseAt ? `${Math.floor((Date.now() - pulseAt) / 1000)}s ago` : "none yet"}
              </div>
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>

        {/* Bridge status indicator */}
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <div className="flex items-center gap-1.5 h-7 px-2 rounded-md bg-surface-2 border border-border">
              <Activity className={cn("size-3", bridgeStatus?.running ? "text-success" : "text-danger")} />
              <span className="text-[11px] font-medium text-fg-muted hidden md:inline">bridge</span>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="bottom"
              sideOffset={6}
              className="panel-elevated px-2.5 py-1.5 text-xs text-fg shadow-md"
            >
              {bridgeStatus?.running ? "agent-bridge is running" : "agent-bridge is stopped"}
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>

        {/* Bus status */}
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <div className="flex items-center gap-1.5 h-7 px-2 rounded-md bg-surface-2 border border-border">
              <AlertCircle className={cn("size-3", busHealth?.healthy ? "text-success" : "text-danger")} />
              <span className="text-[11px] font-medium text-fg-muted hidden md:inline">bus</span>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="bottom"
              sideOffset={6}
              className="panel-elevated px-2.5 py-1.5 text-xs text-fg shadow-md"
            >
              {busHealth?.healthy ? `bus healthy · ${busHealth.message_count} msgs` : "agent-bus unhealthy"}
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>

        {/* Clock */}
        <span className="hidden md:inline text-[11px] font-mono text-fg-muted tabular-nums">{clock}</span>

        {/* Restart dropdown */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-surface-2 hover:bg-surface-elevated border border-border text-fg-muted hover:text-fg ring-focus"
              aria-label="Operations"
            >
              <RotateCcw className="size-3.5" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="panel-elevated p-1 min-w-[200px] shadow-lg z-[300] animate-fade-in"
            >
              <DropdownMenu.Item
                onSelect={() => restart.mutate()}
                className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm text-fg rounded outline-none focus:bg-surface-2 cursor-pointer"
              >
                <span>Restart agent-bridge</span>
                <Kbd>R</Kbd>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {/* Theme toggle */}
        <button
          onClick={toggle}
          className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-surface-2 hover:bg-surface-elevated border border-border text-fg-muted hover:text-fg ring-focus"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
        </button>
      </header>
    </Tooltip.Provider>
  );
}
